import React, {useContext, useEffect, useState} from 'react';
import * as Builder from "../../../builder";
import {ButtonBar} from "../../../builder";
import {Button} from "antd";
import {Translate} from "../../../components/util/translation.util";
import {TranslationDomainEnum} from "../../../types/enums";
import {observer} from "mobx-react";
import {StoreContext} from "../../../store.context";
import {useLoaderData, useNavigate} from "react-router-dom";
import {UserAddRequest} from "../../../types/modules/user/request";
import {TextType} from "../../../builder/form/type/TextType";
import {
    MaxLengthConstraint, RepeatedConstraint,
    RequiredConstraint
} from "../../../builder/form/validation/constraints";
import {EmailType} from "../../../builder/form/type/EmailType";
import {CheckboxType} from "../../../builder/form/type/CheckboxType";
import {EntityType} from "../../../builder/form/type/EntityType";
import {PasswordType} from "../../../builder/form/type/PasswordType";
import {RepeatedType} from "../../../builder/form/type/RepeatedType";
import {PasswordValidationConstraints} from "../../../builder/form/validation/form_type_constraints";
import {UserPrepareAddFormResponse} from "../../../types/modules/user/response";
import {Helmet} from "react-helmet-async";

export const AddForm: React.FC<{ onCancel?: () => void }> = observer(({onCancel}) => {
    const navigate = useNavigate();
    const formReference = React.createRef() as any;
    const data: UserPrepareAddFormResponse = useLoaderData() as UserPrepareAddFormResponse;

    const [fetching, setFetching] = useState<boolean>(true)
    const [classifiers, setClassifiers] = useState<any>([]);
    const [formErrors, setFormErrors] = useState<any>([]);

    const {appStore: {moduleStore: {userStore: {add}}, translate}} = useContext(StoreContext);
    const translationDomain = TranslationDomainEnum.USER_ADMINISTRATION;
    const title = translate('user.title.user_management', {}, translationDomain);

    useEffect(() => {
        setFetching(false);
        setClassifiers(data.classifiers);
    }, [data])

    const onSubmit = (values: UserAddRequest) => {
        setFetching(true);

        add(values)
                .then((response) => {
                    navigate(`/user/${response.id}/view`);
                })
                .catch(error => {
                    setFormErrors(error.response.data.errors);
                })
                .finally(() => {
                    setFetching(false);
                })
    }

    return (
            <>
                <Helmet>
                    <title>{`CL BO - ${title}`}</title>
                </Helmet>
                <Builder.Form
                        translationDomain={TranslationDomainEnum.USER_ADMINISTRATION}
                        formErrors={formErrors}
                        ref={formReference}
                        items={[{
                            title: <Translate message={`global.title.add`}/>,
                            position: 'left',
                            inputs: [
                                {
                                    name: 'credentials.currentPassword',
                                    label: 'user.field.currentPassword',
                                    type: PasswordType,
                                    constraints: [
                                        RequiredConstraint(),
                                    ]
                                },
                                {
                                    name: 'credentials.otp',
                                    label: 'user.field.otp',
                                    type: TextType,
                                },
                                {
                                    name: 'username',
                                    label: 'user.field.username',
                                    type: TextType,
                                    constraints: [
                                        MaxLengthConstraint(255),
                                        RequiredConstraint(),
                                    ]
                                },
                                {
                                    type: RepeatedType,
                                    firstFieldName: 'plainPassword',
                                    secondFieldName: 'plainPasswordRepeat',
                                    firstLabel: 'user.field.plainPassword',
                                    secondLabel: 'user.field.plainPasswordRepeat',
                                    firstFieldConstraints: PasswordValidationConstraints(),
                                    secondFieldConstraints: [
                                        RequiredConstraint(),
                                        RepeatedConstraint('plainPassword'),
                                    ],
                                },
                                {
                                    name: 'firstName',
                                    label: 'user.field.firstName',
                                    type: TextType,
                                    constraints: [
                                        MaxLengthConstraint(150),
                                    ]
                                },
                                {
                                    name: 'lastName',
                                    label: 'user.field.lastName',
                                    type: TextType,
                                    constraints: [
                                        MaxLengthConstraint(150),
                                    ],
                                },
                                {
                                    name: 'initials',
                                    label: 'user.field.initials',
                                    type: TextType,
                                    constraints: [
                                        MaxLengthConstraint(20),
                                    ],
                                },
                                {
                                    name: 'additions',
                                    label: 'user.field.additions',
                                    type: TextType,
                                    constraints: [
                                        MaxLengthConstraint(50),
                                    ],
                                },
                                {
                                    name: 'email',
                                    label: 'user.field.email',
                                    type: EmailType,
                                    constraints: [
                                        MaxLengthConstraint(128),
                                        RequiredConstraint(),
                                    ],
                                },
                                {
                                    name: 'disabled',
                                    label: 'user.field.disabled',
                                    type: CheckboxType
                                },
                                {
                                    name: 'userRoles',
                                    label: 'user.field.userRoles',
                                    type: EntityType,
                                    choices: classifiers.roles,
                                    multiple: true,
                                }]
                        }
                        ]}
                        onFinish={(values: any) => {
                            onSubmit(values)
                        }}
                        initialValues={[]}
                />

                <ButtonBar>
                    <Button type="primary" loading={fetching} htmlType={`submit`} onClick={() => {
                        formReference.current?.submitForm()
                    }}><Translate message={`global.button.save`}/></Button>

                    <Button type="primary" className={`ant-btn-secondary`} onClick={() => {
                        onCancel && onCancel()
                    }}><Translate message={`global.button.back_to_list`}/></Button>
                </ButtonBar>
            </>
    )
})